/* eslint-disable no-unused-vars */
import {createSlice} from '@reduxjs/toolkit';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import {calculateExpiresAt} from '@utils/tokenManager';
import {userApi} from '@api/user';
import {delegationApi} from '@api/delegation/api';
import {accountUserApi} from '@api/accountUser/api';
import {startDelegation, deauthorizeExchangeSession} from '@api/authorizationToken/api';
import {getErrorMessage} from '../../utils/requests/error';
import {getActiveUserIdsSet} from '@utils/account';
import {
    setDelegationUser, setAccessToken, setTokenExpiresAt, fetchSessionInfo
} from '../Login/userSlice';

export const delegationSlice = createSlice({
    name: 'delegation',
    initialState: {
        users: [],
        delegates: [],
        delegatesCount: null,
        delegatesSenders: [],
        delegatesSendersCount: null,
        IsFetchingDelegatesSenders: false,
        isFetchingUsers: false,
        isFetchingDelegates: false,
        isDelegating: false,
        isStoppingDelegation: false,
        isSavingDelegates: false,
        usersFetchingError: null,
        delegatesFetchingError: null,
        delegatingError: null,
        stopDelegationError: null,
        savingDelegatesError: null,
        updatingSenderError: null,
        isUpdatingSender: false
    },
    reducers: {
        setUsers: (state, action) => {
            state.users = action.payload;
        },
        setIsFetchingUsers: (state, action) => {
            state.isFetchingUsers = action.payload;
        },
        setUsersFetchingError: (state, action) => {
            state.usersFetchingError = action.payload;
        },
        setDelegates: (state, action) => {
            state.delegates = action.payload;
        },
        setDelegatesCount: (state, action) => {
            state.delegatesCount = action.payload;
        },
        setIsFetchingDelegates: (state, action) => {
            state.isFetchingDelegates = action.payload;
        },
        setDelegatesFetchingError: (state, action) => {
            state.delegatesFetchingError = action.payload;
        },
        setIsSavingDelegates: (state, action) => {
            state.isSavingDelegates = action.payload;
        },
        setSavingDelegatesError: (state, action) => {
            state.savingDelegatesError = action.payload;
        },
        setIsDelegating: (state, action) => {
            state.isDelegating = action.payload;
        },
        setDelegatingError: (state, action) => {
            state.delegatingError = action.payload;
        },
        setIsStoppingDelegation: (state, action) => {
            state.isStoppingDelegation = action.payload;
        },
        setStopDelegationError: (state, action) => {
            state.stopDelegationError = action.payload;
        },
        setUpdatingSenderError: (state, action) => {
            state.updatingSenderError = action.payload;
        },
        setIsUpdatingSender: (state, action) => {
            state.isUpdatingSender = action.payload;
        },
        setDelegatesSenders: (state, action) => {
            const {payload} = action;
            let updatedDelegatesSenders = [...state.delegatesSenders];
            if (payload.add) {
                updatedDelegatesSenders.unshift(payload.data);
            } else if (payload.override) {
                updatedDelegatesSenders = payload.data;
            } else {
                updatedDelegatesSenders.splice(payload.from - 1, payload.data.length, ...payload.data);
            }
            state.delegatesSenders = updatedDelegatesSenders;
        },
        setDelegatesSendersCount: (state, action) => {
            state.delegatesSendersCount = action.payload;
        },
        setIsFetchingDelegatesSenders: (state, action) => {
            state.IsFetchingDelegatesSenders = action.payload;
        },
        resetDelegatesSenders: (state) => ({
            ...state,
            delegatesSenders: [],
            delegatesSendersCount: 0
        })
    }
});

export const {
    setUsers,
    setIsFetchingUsers,
    setUsersFetchingError,
    setDelegates,
    setDelegatesCount,
    setIsFetchingDelegates,
    setDelegatesFetchingError,
    setIsDelegating,
    setDelegatingError,
    setIsStoppingDelegation,
    setStopDelegationError,
    setIsSavingDelegates,
    setSavingDelegatesError,
    setUpdatingSenderError,
    setIsUpdatingSender,
    setDelegatesSenders,
    setDelegatesSendersCount,
    setIsFetchingDelegatesSenders,
    resetDelegatesSenders
} = delegationSlice.actions;

const getDelegationStatus = (startDate, expiryDate) => {
    const now = new Date();
    const start = startDate ? new Date(startDate) : null;
    const expiry = expiryDate ? new Date(expiryDate) : null;

    if (start && start > now) {
        return 'FUTURE';
    }
    if (expiry && expiry < now) {
        return 'EXPIRED';
    }
    return 'IN_PROGRESS';
};

const getAccountUserId = async (accountId, userId, options = {}) => {
    try {
        const response = await accountUserApi.getAccountUserByAccountAndUser({
            accountId,
            userId,
            options
        });
        return response.data.id;
    } catch {
        return null;
    }
};

const userCache = new Map();
const accountUserCache = new Map();

const getCachedUser = (userId, options = {}) => {
    const isCacheable = isEmpty(options.headers) && !options.accessToken;
    if (isCacheable && userCache.has(userId)) {
        return userCache.get(userId);
    }
    const request = userApi.getUser({userId, options}).catch((err) => {
        userCache.delete(userId);
        throw err;
    });
    if (isCacheable) {
        userCache.set(userId, request);
    }
    return request;
};

const getCachedAccountUser = (accountUserId, options = {}) => {
    const isCacheable = isEmpty(options.headers) && !options.accessToken;
    if (isCacheable && accountUserCache.has(accountUserId)) {
        return accountUserCache.get(accountUserId);
    }
    const request = accountUserApi.getAccountUser({accountUserId, options}).catch((err) => {
        accountUserCache.delete(accountUserId);
        throw err;
    });
    if (isCacheable) {
        accountUserCache.set(accountUserId, request);
    }
    return request;
};

const enrichDelegationWithUserData = async (delegation, {resolveField = 'delegated_account_user_id', ...options} = {}) => {
    const resolveAccountUserId = delegation[resolveField];
    try {
        const accountUserResponse = await getCachedAccountUser(resolveAccountUserId, options);
        const accountUser = accountUserResponse.data;

        const userResponse = await getCachedUser(accountUser.user_id, options);
        const userData = userResponse.data;

        return {
            id: resolveAccountUserId,
            accountUserId: delegation.account_user_id,
            userId: accountUser.user_id,
            delegatedAccountUserId: delegation.delegated_account_user_id,
            delegatedGroupId: delegation.delegated_group_id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            name: `${userData.first_name} ${userData.last_name}`,
            email: userData.email,
            startDate: delegation.start_date,
            expiryDate: delegation.expiry_date,
            sendEmailCopies: delegation.send_email_copy === 'true' || delegation.send_email_copy === true,
            status: getDelegationStatus(delegation.start_date, delegation.expiry_date)
        };
    } catch {
        return {
            id: resolveAccountUserId,
            accountUserId: delegation.account_user_id,
            userId: null,
            delegatedAccountUserId: delegation.delegated_account_user_id,
            delegatedGroupId: delegation.delegated_group_id,
            firstName: '',
            lastName: '',
            name: '',
            email: '',
            startDate: delegation.start_date,
            expiryDate: delegation.expiry_date,
            sendEmailCopies: delegation.send_email_copy === 'true' || delegation.send_email_copy === true,
            status: getDelegationStatus(delegation.start_date, delegation.expiry_date)
        };
    }
};

const getAllDelegators = async (accountUserId, perPage = 100) => {
    const allData = [];
    let currentPage = 1;
    let hasMorePages = true;

    while (hasMorePages) {
        const response = await delegationApi.getDelegators(accountUserId, {
            page: currentPage,
            per_page: perPage
        });
        const pageData = get(response, 'data', []);
        allData.push(...pageData);

        const total = get(response, 'meta.total', 0);
        hasMorePages = pageData.length > 0 && allData.length < total;
        currentPage += 1;
    }

    return allData;
};

const getAllDelegations = async (accountUserId, options = {}, perPage = 100) => {
    const allData = [];
    let currentPage = 1;
    let hasMorePages = true;

    while (hasMorePages) {
        const response = await delegationApi.getDelegations(accountUserId, {
            page: currentPage,
            per_page: perPage
        }, options);
        const pageData = get(response, 'data', []);
        allData.push(...pageData);

        const total = get(response, 'meta.total', 0);
        hasMorePages = pageData.length > 0 && allData.length < total;
        currentPage += 1;
    }

    return allData;
};

/**
 * Fetches all the delegated users for the logged in user
 * @returns {array} delegated users for the logged in user
 */
export const fetchUsers = ({
    headers, baseUrl, options = {}
} = {}) => async (dispatch, getState) => {
    const {user} = getState();
    const userId = get(user, 'delegationUser.id') || get(user, 'user.id', '');
    const accountId = get(user, 'account.id', '');

    dispatch(setIsFetchingUsers(true));
    dispatch(setUsersFetchingError(null));

    try {
        const accountUserId = await getAccountUserId(accountId, userId, {headers});
        if (!accountUserId) {
            dispatch(setUsers([]));
            dispatch(setIsFetchingUsers(false));
            return [];
        }

        const delegators = await getAllDelegations(accountUserId, {
            headers,
            timeout: options.timeout
        });

        const enrichedDelegators = await Promise.all(
            delegators.map(async (delegator) => {
                const enriched = await enrichDelegationWithUserData(delegator, {headers});
                return {
                    ...enriched,
                    originalDelegatedAccountUserId: delegator.delegated_account_user_id
                };
            })
        );
        const validDelegators = enrichedDelegators.filter((delegator) => delegator.name);

        dispatch(setUsers(validDelegators));
        dispatch(setIsFetchingUsers(false));
        return enrichedDelegators;
    } catch (err) {
        dispatch(setIsFetchingUsers(false));
        dispatch(setUsersFetchingError(getErrorMessage(err)));
        return false;
    }
};

export const fetchDelegates = ({from = 1, to, search} = {}) => async (dispatch, getState) => {
    const {user} = getState();
    const userId = get(user, 'delegationUser.id') || get(user, 'user.id', '');
    const accountId = get(user, 'account.id', '');
    const perPage = to ? to - from + 1 : 100;
    const page = Math.ceil(from / perPage);

    dispatch(setIsFetchingDelegates(true));
    dispatch(setUsersFetchingError(null));

    try {
        const accountUserId = await getAccountUserId(accountId, userId);
        if (!accountUserId) {
            dispatch(setDelegates([]));
            dispatch(setDelegatesCount(0));
            dispatch(setIsFetchingDelegates(false));
            return [];
        }

        const response = await delegationApi.getDelegators(accountUserId, {
            page,
            per_page: perPage
        });
        const delegationsData = get(response, 'data', []);
        const delegatesCount = get(response, 'meta.total', 0);

        const enrichedDelegates = await Promise.all(
            delegationsData.map((delegation) => enrichDelegationWithUserData(delegation, {resolveField: 'account_user_id'}))
        );
        const validDelegates = enrichedDelegates.filter((delegate) => delegate.name);

        const filteredDelegates = search
            ? validDelegates.filter((delegate) => delegate.name?.toLowerCase().includes(search.toLowerCase())
                || delegate.email?.toLowerCase().includes(search.toLowerCase()))
            : validDelegates;

        dispatch(setDelegates(filteredDelegates));
        dispatch(setDelegatesCount(search ? filteredDelegates.length : delegatesCount));
        dispatch(setIsFetchingDelegates(false));
        return filteredDelegates;
    } catch (err) {
        dispatch(setDelegatesFetchingError(getErrorMessage(err)));
        dispatch(setIsFetchingDelegates(false));
        return false;
    }
};

export const fetchDelegatesSendersBySenderId = ({
    from, to, search, override, senderId
}) => async (dispatch, getState) => {
    const {user} = getState();
    const accountId = get(user, 'account.id', '');
    const perPage = to ? to - from + 1 : 20;
    const page = Math.ceil(from / perPage);

    dispatch(setIsFetchingDelegatesSenders(true));

    try {
        let senders = [];
        let totalCount = 0;

        if (search) {
            const [activeUserIds, usersResponse] = await Promise.all([
                getActiveUserIdsSet(accountId),
                userApi.getUsers({params: {search, per_page: 100}})
            ]);
            const users = get(usersResponse, 'data', []);
            senders = users
                .filter((u) => activeUserIds.has(u.id))
                .map((u) => ({
                    id: u.id,
                    firstName: u.first_name,
                    lastName: u.last_name,
                    name: `${u.first_name} ${u.last_name}`,
                    email: u.email
                }));
            totalCount = senders.length;
        } else {
            const response = await accountUserApi.getAccountUsers({
                accountId,
                params: {page, per_page: perPage, scope: 'active'}
            });
            const accountUsers = get(response, 'data', []);
            totalCount = get(response, 'meta.total', 0);

            const enrichedSenders = await Promise.all(
                accountUsers.map(async (accountUser) => {
                    try {
                        const userResponse = await getCachedUser(accountUser.user_id);
                        const userData = userResponse.data;
                        return {
                            id: accountUser.user_id,
                            accountUserId: accountUser.id,
                            firstName: userData.first_name,
                            lastName: userData.last_name,
                            name: `${userData.first_name} ${userData.last_name}`,
                            email: userData.email
                        };
                    } catch {
                        return null;
                    }
                })
            );
            senders = enrichedSenders.filter(Boolean);
        }

        dispatch(setDelegatesSenders({from, data: senders, override}));
        dispatch(setDelegatesSendersCount(totalCount));
        dispatch(setIsFetchingDelegatesSenders(false));

        return senders;
    } catch (err) {
        dispatch(setIsFetchingDelegatesSenders(false));

        return false;
    }
};

export const fetchDelegatesSenders = (params) => async (dispatch, getState) => {
    const {user} = getState();
    const senderId = get(user, 'delegationUser.id') || get(user, 'user.id', '');

    return dispatch(fetchDelegatesSendersBySenderId({
        ...params,
        senderId
    }));
};

export const delegateUser = (userid) => async (dispatch, getState) => {
    dispatch(setIsDelegating(true));
    dispatch(setDelegatingError(null));

    try {
        const {delegation, user} = getState();
        const users = delegation.users || [];
        const selectedUser = users.find((u) => u.id === userid);
        const delegatedAccountUserId = selectedUser?.originalDelegatedAccountUserId;
        const accountId = get(user, 'account.id', '');

        if (!delegatedAccountUserId) {
            dispatch(setIsDelegating(false));
            dispatch(setDelegatingError('Could not get delegated account user ID'));
            return false;
        }

        const isDelegationActive = !isEmpty(user.delegationUser);
        if (isDelegationActive) {
            const deauthResponse = await deauthorizeExchangeSession();
            const deauthTokenData = deauthResponse?.data;
            if (deauthTokenData?.access_token) {
                dispatch(setAccessToken(deauthTokenData.access_token));
                if (deauthTokenData.expires_in) {
                    dispatch(setTokenExpiresAt(calculateExpiresAt(deauthTokenData.expires_in)));
                }
            }
            await dispatch(fetchSessionInfo());
        }

        const response = await startDelegation(delegatedAccountUserId, accountId);
        const tokenData = response?.data;

        if (tokenData?.access_token) {
            dispatch(setAccessToken(tokenData.access_token));
            if (tokenData.expires_in) {
                dispatch(setTokenExpiresAt(calculateExpiresAt(tokenData.expires_in)));
            }
        }

        dispatch(setDelegationUser(selectedUser));
        await dispatch(fetchSessionInfo());

        dispatch(setIsDelegating(false));
        return true;
    } catch (err) {
        dispatch(setIsDelegating(false));
        dispatch(setDelegatingError(getErrorMessage(err)));
        return false;
    }
};

export const stopDelegation = () => async (dispatch) => {
    dispatch(setIsStoppingDelegation(true));
    dispatch(setStopDelegationError(null));

    try {
        const response = await deauthorizeExchangeSession();
        const tokenData = response?.data;

        if (tokenData?.access_token) {
            dispatch(setAccessToken(tokenData.access_token));
            if (tokenData.expires_in) {
                dispatch(setTokenExpiresAt(calculateExpiresAt(tokenData.expires_in)));
            }
        }

        await dispatch(fetchSessionInfo());
        dispatch(setIsStoppingDelegation(false));
        return true;
    } catch (err) {
        dispatch(setIsStoppingDelegation(false));
        dispatch(setStopDelegationError(getErrorMessage(err)));
        return false;
    }
};
export const saveDelegates = (payload) => async (dispatch, getState) => {
    const {user} = getState();
    const userId = get(user, 'delegationUser.id') || get(user, 'user.id', '');
    const accountId = get(user, 'account.id', '');
    dispatch(setIsSavingDelegates(true));
    dispatch(setSavingDelegatesError(null));
    try {
        const accountUserId = await getAccountUserId(accountId, userId);
        if (!accountUserId) {
            dispatch(setSavingDelegatesError('Could not get account user ID'));
            dispatch(setIsSavingDelegates(false));
            return false;
        }

        const delegatesArray = Array.isArray(payload) ? payload : [payload];

        const delegatesWithAccountUserIds = await Promise.all(
            delegatesArray.map(async (delegate) => {
                if (delegate.accountUserId) {
                    return delegate;
                }
                const delegateAccountUserId = await getAccountUserId(accountId, delegate.id);
                return {
                    ...delegate,
                    accountUserId: delegateAccountUserId
                };
            })
        );

        await Promise.all(delegatesWithAccountUserIds
            .filter((delegate) => delegate.accountUserId)
            .map((delegate) => delegationApi.createDelegation(delegate.accountUserId, {
                delegated_account_user_id: accountUserId,
                start_date: delegate.startDate || null,
                expiry_date: delegate.expiryDate || null,
                send_email_copy: delegate.sendEmailCopies ?? false
            })));
        dispatch(setIsSavingDelegates(false));
        dispatch(fetchDelegates());
        return true;
    } catch (err) {
        dispatch(setSavingDelegatesError(getErrorMessage(err)));
        dispatch(setIsSavingDelegates(false));
        return false;
    }
};
export const updateDelegates = (remainingDelegates) => async (dispatch, getState) => {
    const {user, delegation} = getState();
    const userId = get(user, 'delegationUser.id') || get(user, 'user.id', '');
    const accountId = get(user, 'account.id', '');
    const currentDelegates = delegation.delegates || [];
    dispatch(setIsSavingDelegates(true));
    dispatch(setSavingDelegatesError(null));

    try {
        const accountUserId = await getAccountUserId(accountId, userId);
        if (!accountUserId) {
            dispatch(setSavingDelegatesError('Could not get account user ID'));
            dispatch(setIsSavingDelegates(false));
            return false;
        }

        const remainingIds = remainingDelegates.map((d) => d.id || d.delegatedAccountUserId);
        const delegatesToRemove = currentDelegates.filter(
            (d) => !remainingIds.includes(d.id) && !remainingIds.includes(d.delegatedAccountUserId)
        );

        await Promise.all(delegatesToRemove.map((delegate) => {
            const delegateAccountUserId = delegate.id || delegate.delegatedAccountUserId;
            return delegationApi.revokeDelegation(delegateAccountUserId, {
                delegated_account_user_id: accountUserId
            });
        }));

        dispatch(setIsSavingDelegates(false));
        dispatch(fetchDelegates());
        return true;
    } catch (err) {
        dispatch(setSavingDelegatesError(getErrorMessage(err)));
        dispatch(setIsSavingDelegates(false));
        return false;
    }
};
export const updateDelegatesForAccount = (delegates) => async (dispatch, getState) => {
    const {user} = getState();
    const userId = get(user, 'delegationUser.id') || get(user, 'user.id', '');
    const accountId = get(user, 'account.id', '');
    dispatch(setIsSavingDelegates(true));
    dispatch(setSavingDelegatesError(null));

    try {
        const accountUserId = await getAccountUserId(accountId, userId);
        if (!accountUserId) {
            dispatch(setSavingDelegatesError('Could not get account user ID'));
            dispatch(setIsSavingDelegates(false));
            return false;
        }

        await Promise.all(delegates.map(async (delegate) => {
            const delegatedAccountUserId = delegate.id || delegate.delegatedAccountUserId;
            await delegationApi.revokeDelegation(delegatedAccountUserId, {
                delegated_account_user_id: accountUserId
            });
            await delegationApi.createDelegation(delegatedAccountUserId, {
                delegated_account_user_id: accountUserId,
                start_date: delegate.startDate,
                expiry_date: delegate.expiryDate,
                send_email_copy: delegate.sendEmailCopies
            });
        }));
        dispatch(setIsSavingDelegates(false));
        dispatch(fetchDelegates());
        return true;
    } catch (err) {
        dispatch(setSavingDelegatesError(getErrorMessage(err)));
        dispatch(setIsSavingDelegates(false));
        return false;
    }
};

export const updateSenderDelegates = (senderId, delegates, visibleDelegateAccountUserIds = null) => async (dispatch, getState) => {
    const {user} = getState();
    const accountId = get(user, 'account.id', '');

    dispatch(setIsUpdatingSender(true));
    dispatch(setUpdatingSenderError(null));

    try {
        const senderAccountUserId = await getAccountUserId(accountId, senderId);
        if (!senderAccountUserId) {
            dispatch(setUpdatingSenderError('Could not get account user ID for sender'));
            dispatch(setIsUpdatingSender(false));
            return false;
        }

        const currentDelegates = await getAllDelegators(senderAccountUserId);

        const delegatesToAddAccountUserIds = await Promise.all(
            delegates.map(async (d) => {
                if (d.accountUserId) {
                    return d;
                }
                const delegateUserId = d.userId || d.id;
                const delegateAccountUserId = await getAccountUserId(accountId, delegateUserId);
                return {
                    ...d,
                    accountUserId: delegateAccountUserId
                };
            })
        );

        const newDelegateAccountUserIds = delegatesToAddAccountUserIds
            .map((d) => d.accountUserId)
            .filter(Boolean);
        const delegatesToRemove = currentDelegates.filter(
            (d) => !newDelegateAccountUserIds.includes(d.account_user_id)
                && (visibleDelegateAccountUserIds === null || visibleDelegateAccountUserIds.includes(d.account_user_id))
        );

        await Promise.all(delegatesToRemove.map((delegate) => delegationApi.revokeDelegation(delegate.account_user_id, {
            delegated_account_user_id: senderAccountUserId
        })));

        const currentDelegateIds = currentDelegates.map((d) => d.account_user_id);
        const delegatesToAdd = delegatesToAddAccountUserIds.filter(
            (d) => d.accountUserId && !currentDelegateIds.includes(d.accountUserId)
        );

        const today = new Date().toISOString().split('T')[0];
        const getValidStartDate = (startDate) => {
            if (!startDate) return null;
            const start = new Date(startDate);
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            return start >= now ? startDate : today;
        };

        await Promise.all(delegatesToAdd.map((delegate) => delegationApi.createDelegation(delegate.accountUserId, {
            delegated_account_user_id: senderAccountUserId,
            start_date: getValidStartDate(delegate.startDate),
            expiry_date: delegate.expiryDate || null,
            send_email_copy: delegate.sendEmailCopies ?? false
        })));

        dispatch(setIsUpdatingSender(false));
        dispatch(fetchDelegates());
        return true;
    } catch (err) {
        dispatch(setUpdatingSenderError(getErrorMessage(err)));
        dispatch(setIsUpdatingSender(false));
        return false;
    }
};

/**
 * The isCheckForRights param is optional and is used to prevent the error message to be shown.
 * This is due to a specific case where a hotfix is to call this function to see when it breaks.
 * It's related to story RFT-152
 */
export const fetchSenderDelegates = (userId, options = {}) => async (dispatch, getState) => {
    const {user} = getState();
    const accountId = get(user, 'account.id', '');
    const {isCheckForRights} = options;

    dispatch(setIsFetchingDelegates(true));
    dispatch(setUsersFetchingError(null));

    try {
        const accountUserId = await getAccountUserId(accountId, userId);
        if (!accountUserId) {
            dispatch(setIsFetchingDelegates(false));
            return [];
        }

        const delegationsData = await getAllDelegators(accountUserId);

        const enrichedDelegates = await Promise.all(
            delegationsData.map((delegation) => enrichDelegationWithUserData(delegation, {resolveField: 'account_user_id'}))
        );
        const validDelegates = enrichedDelegates.filter(({firstName, lastName}) => firstName || lastName);

        dispatch(setIsFetchingDelegates(false));
        return validDelegates;
    } catch (err) {
        if (!isCheckForRights) {
            dispatch(setDelegatesFetchingError(getErrorMessage(err)));
        }
        dispatch(setIsFetchingDelegates(false));

        return false;
    }
};
export const resetPassword = (email) => async (dispatch) => {
    dispatch(setUpdatingSenderError(null));
    dispatch(setIsUpdatingSender(true));

    try {
        await userApi.forgotPassword({data: {email}});
        dispatch(setIsUpdatingSender(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingSender(false));
        dispatch(setUpdatingSenderError(getErrorMessage(err)));
        return false;
    }
};

export const selectUsers = (state) => state.delegation.users;
export const selectIsFetchingUsers = (state) => state.delegation.isFetchingUsers;
export const selectDelegates = (state) => state.delegation.delegates;
export const selectDelegatesCount = (state) => state.delegation.delegatesCount;
export const selectDelegatesFetchingError = (state) => state.delegation.delegatesFetchingError;
export const selectIsFetchingDelegates = (state) => state.delegation.isFetchingDelegates;
export const selectIsSavingDelegates = (state) => state.delegation.isSavingDelegates;
export const selectSavingDelegatesError = (state) => state.delegation.savingDelegatesError;
export const selectUpdatingSenderError = (state) => state.delegation.updatingSenderError;
export const selectIsUpdatingSender = (state) => state.delegation.isUpdatingSender;
export const selectDelegatesSenders = (state) => state.delegation.delegatesSenders;
export const selectDelegatesSendersCount = (state) => state.delegation.delegatesSendersCount;
export const selectIsFetchingDelegatesSenders = (state) => state.delegation.IsFetchingDelegatesSenders;

export default delegationSlice.reducer;
